import deliveryAreaConfigAtom from "@/atoms/delivery-area-config"
import { fetchDeliveryAreaConfig } from "@/lib/delivery-area/fetch-delivery-area"
import { getMerchantId } from "@/lib/company-storage"
import { useSetAtom } from "jotai"
import { useCallback, useEffect, useRef } from "react"

const REFRESH_INTERVAL_MS = 60_000

let refreshHandle: (() => Promise<void>) | null = null

/** Forces an immediate re-read of the delivery-area config, if the hook is mounted. */
export function refreshDeliveryAreaConfig(): Promise<void> {
  return refreshHandle ? refreshHandle() : Promise.resolve()
}

/**
 * Loads how the store delivers on mount, then keeps it fresh on the same
 * cadence as the catalog poll: every 60 s while visible, and on tab focus.
 * The response is cache-backed on the gateway, so this stays cheap. A failed
 * read keeps the last good config; the atom stays `null` only until the first
 * success, and `service-area.ts` owns that fail-safe.
 */
export function useDeliveryAreaConfig(enabled: boolean): void {
  const setConfig = useSetAtom(deliveryAreaConfigAtom)
  const inFlight = useRef<AbortController | null>(null)

  const refresh = useCallback(async () => {
    const merchantId = getMerchantId()
    if (!merchantId) return

    inFlight.current?.abort()
    const controller = new AbortController()
    inFlight.current = controller

    try {
      const config = await fetchDeliveryAreaConfig(merchantId, controller.signal)
      if (controller.signal.aborted) return
      setConfig(config)
    } catch {
      return
    }
  }, [setConfig])

  useEffect(() => {
    refreshHandle = refresh
    return () => {
      if (refreshHandle === refresh) refreshHandle = null
    }
  }, [refresh])

  useEffect(() => {
    if (!enabled) return
    void refresh()

    function onVisible() {
      if (document.visibilityState === "visible") void refresh()
    }

    const interval = window.setInterval(() => {
      if (document.visibilityState !== "visible") return
      void refresh()
    }, REFRESH_INTERVAL_MS)

    document.addEventListener("visibilitychange", onVisible)
    window.addEventListener("focus", onVisible)

    return () => {
      window.clearInterval(interval)
      document.removeEventListener("visibilitychange", onVisible)
      window.removeEventListener("focus", onVisible)
      inFlight.current?.abort()
    }
  }, [enabled, refresh])
}
